import { Button, Form, Spinner } from 'react-bootstrap';
import { CustomerCEParticipationCsv, VCOCEParticipation, VCOProject } from './CustomProjectModels';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { Slide, toast } from 'react-toastify';

import { AddFilled } from '@carbon/icons-react';
import ApiHelper from '../../../Shared/ApiHelper';
import DatePicker from 'react-datepicker';
import DeleteButton from '../../../Shared/components/DeleteButton';
import SaveButton from '../../../Shared/components/SaveButton';
import Select from 'react-select';
import { SelectOption } from '../../../Shared/SharedModels';
import { UserProfileContext } from '../../../Shared/contexts/UserProfile';
import { useMsal } from '@azure/msal-react';

export interface CEParticipationEditFormProps {
    vcoProject: VCOProject;
    batchKey: number;
}

function getCEParticipationOptionLabel(csv: CustomerCEParticipationCsv): string {
    return `CEParticipation ID: ${csv.ceParticipationID || 'New CE Participation'} - ${
        csv.ceParticipationStatusEndDate ? 'Inactive' : 'Active'
    }`;
}

const CEParticipationEditForm: FunctionComponent<CEParticipationEditFormProps> = ({ vcoProject, batchKey }) => {
    const userProfile = useContext(UserProfileContext);

    const defaultCEPCsv = useMemo<CustomerCEParticipationCsv>(() => {
        return { batchKey: 0, sourceSystemShortName: '', projectID: '' };
    }, []);

    const [selectedCEParticipation, setSelectedCEParticipation] = useState<CustomerCEParticipationCsv | undefined>();
    const [cepOptions, setCEPOptions] = useState<SelectOption<CustomerCEParticipationCsv>[]>([]);

    const [isSaving, setIsSaving] = useState(false);
    const [canSave, setCanSave] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const api = useMemo(() => new ApiHelper(), []);
    const { instance } = useMsal();

    useEffect(() => {
        setCanSave(
            !!selectedCEParticipation?.ceParticipationStatusStartDate &&
                !!selectedCEParticipation?.accessPoint &&
                !!selectedCEParticipation?.receivesReferrals
        );
    }, [
        selectedCEParticipation?.accessPoint,
        selectedCEParticipation?.ceParticipationStatusStartDate,
        selectedCEParticipation?.receivesReferrals,
    ]);

    useEffect(() => {
        if (isLoading && vcoProject.projectKey && batchKey) {
            api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_NET_API_URL + `/CustomProject/projects/${vcoProject.projectKey}/ceParticipation`,
                'GET'
            )
                .then(async (result: Response) => {
                    const participations: VCOCEParticipation[] = await result.json();

                    const customParticipations: CustomerCEParticipationCsv[] = participations.map((vco) => ({
                        batchKey: batchKey,
                        ceParticipationID: vco.ceParticipationID,
                        sourceSystemShortName:
                            userProfile.sourceSystems.find((s) => s.sourceSystemKey === vcoProject.sourceSystemKey)
                                ?.sourceSystemShortName ?? '',
                        projectID: vco.projectID,
                        accessPoint: vco.accessPointKey?.toString(),
                        preventionAssessment: vco.preventionAssessmentKey?.toString(),
                        crisisAssessment: vco.crisisAssessmentKey?.toString(),
                        housingAssessment: vco.housingAssessmentKey?.toString(),
                        directServices: vco.directServicesKey?.toString(),
                        receivesReferrals: vco.receivesReferralsKey?.toString(),
                        ceParticipationStatusStartDate: vco.ceParticipationStatusStartDate,
                        ceParticipationStatusEndDate: vco.ceParticipationStatusEndDate,
                        dateCreated: vco.dateCreated,
                        dateUpdated: vco.dateUpdated,
                        userID: vco.userID,
                        dateDeleted: vco.dateDeleted,
                    }));

                    if (customParticipations.length) {
                        setCEPOptions(
                            customParticipations.map((csv) => ({
                                label: getCEParticipationOptionLabel(csv),
                                value: csv,
                            }))
                        );
                        setSelectedCEParticipation(customParticipations[0]);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [
        api,
        batchKey,
        instance,
        isLoading,
        userProfile.sourceSystems,
        vcoProject.projectKey,
        vcoProject.sourceSystemKey,
    ]);

    return (
        <section className="project-card mb-5">
            <header className="project-card-header">
                <h3>CEParticipation CSV Data</h3>
                {!!batchKey && !isLoading && (
                    <div className="d-flex align-items-center">
                        {selectedCEParticipation?.ceParticipationID && (
                            <DeleteButton
                                isLoading={isSaving}
                                isDisabled={!canSave}
                                onClick={() => {
                                    setIsSaving(true);

                                    const dateDeleted = new Date().toISOString();
                                    const payload: CustomerCEParticipationCsv = {
                                        ...selectedCEParticipation,
                                        dateDeleted: dateDeleted,
                                        dateUpdated: dateDeleted,
                                    };

                                    api.callApi(
                                        instance,
                                        [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                        process.env.REACT_APP_NET_API_URL + `/CustomProject/ceParticipation`,
                                        'POST',
                                        JSON.stringify(payload)
                                    )
                                        .then(() => {
                                            toast.success('CEParticipation Data Deleted Successfully', {
                                                position: 'top-right',
                                                autoClose: 5000,
                                                transition: Slide,
                                                draggable: false,
                                                closeOnClick: false,
                                                theme: 'colored',
                                            });

                                            setIsLoading(true);
                                        })
                                        .finally(() => {
                                            setIsSaving(false);
                                        });
                                }}
                            />
                        )}
                        <Button
                            className="form-action-button mx-2"
                            variant="outline-primary"
                            disabled={isSaving}
                            onClick={() => {
                                const currentDate = new Date().toISOString().split('T')[0];
                                const newCEP: CustomerCEParticipationCsv = {
                                    batchKey,
                                    sourceSystemShortName:
                                        userProfile.sourceSystems.find(
                                            (s) => s.sourceSystemKey === vcoProject.sourceSystemKey
                                        )?.sourceSystemShortName ?? '',
                                    projectID: vcoProject.projectId ?? '',
                                    dateCreated: currentDate,
                                    dateUpdated: currentDate,
                                    userID: vcoProject.userID,
                                };

                                setSelectedCEParticipation(newCEP);
                            }}
                        >
                            <AddFilled size={24} className="button-icon-padding" /> Add CEParticipation
                        </Button>
                        {selectedCEParticipation && (
                            <SaveButton
                                isSaving={isSaving}
                                isDisabled={!canSave}
                                onClick={() => {
                                    setIsSaving(true);

                                    const payload: CustomerCEParticipationCsv = {
                                        ...selectedCEParticipation,
                                    };

                                    api.callApi(
                                        instance,
                                        [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                        process.env.REACT_APP_NET_API_URL + `/CustomProject/ceParticipation`,
                                        'POST',
                                        JSON.stringify(payload)
                                    )
                                        .then(() => {
                                            toast.success('CEParticipation Data Saved Successfully', {
                                                position: 'top-right',
                                                autoClose: 5000,
                                                transition: Slide,
                                                draggable: false,
                                                closeOnClick: false,
                                                theme: 'colored',
                                            });

                                            setIsLoading(true);
                                        })
                                        .finally(() => {
                                            setIsSaving(false);
                                        });
                                }}
                            />
                        )}
                    </div>
                )}
            </header>
            {isLoading ? (
                <div className="d-flex justify-content-center p-3">
                    <Spinner
                        variant="primary"
                        size="sm"
                        animation="border"
                        data-testid="ceParticipationEditFormSpinner"
                    />
                </div>
            ) : (
                <main className="project-card-body">
                    {cepOptions.length || selectedCEParticipation ? (
                        <>
                            <Form.Group className="wide-card-form-group">
                                <Form.Label className={isSaving ? 'disabled-label' : ''} htmlFor="ceParticipationSelector">CEParticipation ID</Form.Label>
                                <Select
                                    inputId="ceParticipationSelector"
                                    isClearable
                                    isLoading={isLoading}
                                    isDisabled={isSaving}
                                    className="filterSelect"
                                    placeholder="Select CEParticipation..."
                                    value={
                                        selectedCEParticipation
                                            ? {
                                                  label: getCEParticipationOptionLabel(selectedCEParticipation),
                                                  value: selectedCEParticipation,
                                              }
                                            : undefined
                                    }
                                    options={cepOptions}
                                    onChange={(selectedOption) => {
                                        setSelectedCEParticipation(selectedOption?.value);
                                    }}
                                />
                            </Form.Group>
                            {selectedCEParticipation && (
                                <Form className="card-form row">
                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="accessPoint">Access Point*</Form.Label>
                                            <Form.Control
                                                id="accessPoint"
                                                data-testid="accessPointInput"
                                                placeholder="Enter Access Point"
                                                isInvalid={!selectedCEParticipation.accessPoint}
                                                value={selectedCEParticipation.accessPoint ?? ''}
                                                onChange={(event) => {
                                                    const numberValue = parseInt(event.target.value);
                                                    const isNumber = !isNaN(numberValue);

                                                    if (isNumber) {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            accessPoint: numberValue.toString(),
                                                        }));
                                                    } else if (event.target.value === '') {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            accessPoint: undefined,
                                                        }));
                                                    }
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="preventionAssessment">
                                                Prevention Assessment
                                            </Form.Label>
                                            <Form.Control
                                                id="preventionAssessment"
                                                data-testid="preventionAssessmentInput"
                                                placeholder="Enter Prevention Assessment"
                                                value={selectedCEParticipation.preventionAssessment ?? ''}
                                                onChange={(event) => {
                                                    const numberValue = parseInt(event.target.value);
                                                    const isNumber = !isNaN(numberValue);

                                                    if (isNumber) {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            preventionAssessment: numberValue.toString(),
                                                        }));
                                                    } else if (event.target.value === '') {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            preventionAssessment: undefined,
                                                        }));
                                                    }
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="crisisAssessment">Crisis Assessment</Form.Label>
                                            <Form.Control
                                                id="crisisAssessment"
                                                data-testid="crisisAssessmentInput"
                                                placeholder="Enter Crisis Assessment"
                                                value={selectedCEParticipation.crisisAssessment ?? ''}
                                                onChange={(event) => {
                                                    const numberValue = parseInt(event.target.value);
                                                    const isNumber = !isNaN(numberValue);

                                                    if (isNumber) {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            crisisAssessment: numberValue.toString(),
                                                        }));
                                                    } else if (event.target.value === '') {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            crisisAssessment: undefined,
                                                        }));
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                    </div>

                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="housingAssessment">Housing Assessment</Form.Label>
                                            <Form.Control
                                                id="housingAssessment"
                                                data-testid="housingAssessmentInput"
                                                placeholder="Enter Housing Assessment"
                                                value={selectedCEParticipation.housingAssessment ?? ''}
                                                onChange={(event) => {
                                                    const numberValue = parseInt(event.target.value);
                                                    const isNumber = !isNaN(numberValue);

                                                    if (isNumber) {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            housingAssessment: numberValue.toString(),
                                                        }));
                                                    } else if (event.target.value === '') {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            housingAssessment: undefined,
                                                        }));
                                                    }
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="directServices">Direct Services</Form.Label>
                                            <Form.Control
                                                id="directServices"
                                                data-testid="directServicesInput"
                                                placeholder="Enter Direct Services"
                                                value={selectedCEParticipation.directServices ?? ''}
                                                onChange={(event) => {
                                                    const numberValue = parseInt(event.target.value);
                                                    const isNumber = !isNaN(numberValue);

                                                    if (isNumber) {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            directServices: numberValue.toString(),
                                                        }));
                                                    } else if (event.target.value === '') {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            directServices: undefined,
                                                        }));
                                                    }
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="receivesReferrals">Receives Referrals*</Form.Label>
                                            <Form.Control
                                                id="receivesReferrals"
                                                data-testid="receivesReferralsInput"
                                                placeholder="Enter Receives Referrals"
                                                isInvalid={!selectedCEParticipation.receivesReferrals}
                                                value={selectedCEParticipation.receivesReferrals ?? ''}
                                                onChange={(event) => {
                                                    const numberValue = parseInt(event.target.value);
                                                    const isNumber = !isNaN(numberValue);

                                                    if (isNumber) {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            receivesReferrals: numberValue.toString(),
                                                        }));
                                                    } else if (event.target.value === '') {
                                                        setSelectedCEParticipation((prev) => ({
                                                            ...defaultCEPCsv,
                                                            ...prev,
                                                            receivesReferrals: undefined,
                                                        }));
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                    </div>

                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="startDate">
                                                CEParticipation Status Start Date*
                                            </Form.Label>
                                            <DatePicker
                                                id="startDate"
                                                placeholderText="Start Date"
                                                className="form-control"
                                                dateFormat="MM-dd-yyyy"
                                                selected={
                                                    selectedCEParticipation.ceParticipationStatusStartDate
                                                        ? new Date(
                                                              selectedCEParticipation.ceParticipationStatusStartDate
                                                          )
                                                        : null
                                                }
                                                onChange={(date: Date | null) => {
                                                    setSelectedCEParticipation((prev) => ({
                                                        ...defaultCEPCsv,
                                                        ...prev,
                                                        ceParticipationStatusStartDate: date
                                                            ? date.toISOString()
                                                            : undefined,
                                                    }));
                                                }}
                                            />
                                        </Form.Group>
                                    </div>

                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="endDate">CEParticipation Status End Date</Form.Label>
                                            <DatePicker
                                                id="endDate"
                                                placeholderText="End Date"
                                                className="form-control"
                                                dateFormat="MM-dd-yyyy"
                                                selected={
                                                    selectedCEParticipation.ceParticipationStatusEndDate
                                                        ? new Date(selectedCEParticipation.ceParticipationStatusEndDate)
                                                        : null
                                                }
                                                onChange={(date: Date | null) => {
                                                    setSelectedCEParticipation((prev) => ({
                                                        ...defaultCEPCsv,
                                                        ...prev,
                                                        ceParticipationStatusEndDate: date
                                                            ? date.toISOString()
                                                            : undefined,
                                                    }));
                                                }}
                                            />
                                        </Form.Group>
                                    </div>
                                </Form>
                            )}
                        </>
                    ) : (
                        <p className="text-center p-3">No CEParticipation records found for this project</p>
                    )}
                </main>
            )}
        </section>
    );
};

export default CEParticipationEditForm;
